<template>
    <div>
        <div class="tw-mb-6">
            <div class="tw-flex tw-flex-col tw-gap-2">
                <h1
                    class="text-6xl font-black leading-tight text-slate-900 dark:text-white"
                    style="-webkit-text-stroke: 1.5px currentColor;"
                >
                    {{ $t("zone.dispatchOverview") }}
                </h1>
                <p
                    style="font-size: 15px;"
                    class="text-slate-500 dark:text-slate-400"
                >
                    {{ $t("zone.zoneOverViewDes") }}
                </p>
            </div>
        </div>

        <main
            class="tw-bg-white dark:tw-bg-gray-800 tw-rounded-lg tw-p-6 lg:tw-p-8"
        >
            <div class="tw-flex tw-flex-col tw-gap-8">
                <div
                    class="tw-flex tw-flex-wrap tw-items-center tw-gap-3 tw-justify-end"
                >
                    <div
                        class="tw-flex tw-items-center tw-gap-2 tw-rounded-lg tw-bg-gray-50 dark:tw-bg-slate-700 tw-border tw-border-slate-200 dark:tw-border-slate-600 tw-p-1 tw-pr-3 tw-shadow-sm"
                    >
                        <div
                            class="tw-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded tw-bg-gray-100 dark:tw-bg-gray-600"
                        >
                            <span
                                class="material-symbols-outlined tw-text-[18px] tw-text-gray-500 dark:tw-text-gray-300"
                                >timer</span
                            >
                        </div>
                        <select
                            v-model="refreshInterval"
                            class="tw-bg-transparent tw-border-none tw-text-sm tw-font-semibold tw-text-slate-700 dark:tw-text-slate-200 focus:tw-ring-0 tw-outline-none tw-cursor-pointer tw-py-1 tw-pl-1 tw-pr-6"
                        >
                            <option value="30"
                                >{{ $t("zone.refresh") }}: 30s</option
                            >
                            <option value="60"
                                >{{ $t("zone.refresh") }}: 1m</option
                            >
                            <option value="120"
                                >{{ $t("zone.refresh") }}: 2m</option
                            >
                        </select>
                    </div>
                    <button
                        @click="addNewZone"
                        class="tw-flex tw-h-11 tw-items-center tw-gap-2 tw-rounded-lg tw-bg-blue-500 tw-px-5 tw-text-sm tw-font-bold tw-text-white tw-shadow-md tw-shadow-blue-500/20 hover:tw-bg-blue-600 tw-transition-colors"
                    >
                        <span class="material-symbols-outlined tw-text-[20px]"
                            >add_location_alt</span
                        >
                        <span>{{ $t("zone.addNewZone") }}</span>
                    </button>
                </div>
                <div
                    class="tw-flex tw-flex-col tw-gap-4 tw-rounded-xl tw-bg-gray-50 dark:tw-bg-slate-700 tw-p-4 tw-shadow-sm tw-border tw-border-slate-200 dark:tw-border-slate-600 lg:tw-flex-row lg:tw-items-center lg:tw-justify-between"
                >
                    <!-- Search Input - Responsive width -->
                    <div class="tw-relative tw-w-full lg:tw-w-48 xl:tw-w-64 2xl:tw-max-w-md tw-flex-shrink-0">
                        <div
                            class="tw-pointer-events-none tw-absolute tw-inset-y-0 tw-left-0 tw-flex tw-items-center tw-pl-3"
                        >
                            <span
                                class="material-symbols-outlined tw-text-slate-400"
                                >search</span
                            >
                        </div>
                        <input
                            v-model="search"
                            class="tw-block tw-w-full tw-rounded-lg tw-border tw-border-gray-200 dark:tw-border-gray-600 tw-bg-white dark:tw-bg-gray-800 tw-py-2.5 tw-pl-10 tw-pr-3 tw-text-sm tw-text-gray-600 dark:tw-text-white placeholder:tw-text-gray-400 focus:tw-outline-none"
                            :placeholder="$t('zone.zoneSearch')"
                            type="text"
                        />
                    </div>

                    <!-- Filter Buttons - Right aligned on large screens -->
                    <div class="tw-overflow-x-auto tw-pb-2 lg:tw-pb-0 scrollbar-hide tw--mx-4 tw-px-4 lg:tw-mx-0 lg:tw-px-0">
                        <div class="tw-flex tw-gap-2 lg:tw-justify-end" style="width: max-content;">
                            <button
                                v-for="filter in filtersWithCounts"
                                :key="filter.id"
                                @click="activeFilter = filter.id"
                                :class="[
                                    'tw-whitespace-nowrap tw-rounded-full tw-px-3 sm:tw-px-4 tw-py-1.5 tw-text-xs sm:tw-text-sm tw-font-medium tw-transition-all tw-flex tw-items-center tw-flex-shrink-0',

                                    // Selected state: Solid Black with White text
                                    activeFilter === filter.id
                                        ? 'tw-bg-black tw-text-white'
                                        : 'tw-bg-gray-100 dark:tw-bg-gray-600 tw-text-gray-600 dark:tw-text-gray-200 hover:tw-bg-gray-200 dark:hover:tw-bg-gray-500'
                                ]"
                            >
                                {{ filter.label }}

                                <span
                                    v-if="filter.count > 0"
                                    :class="[
                                        'tw-ml-2 tw-rounded-full tw-px-1.5 tw-py-0.5 tw-font-bold tw-flex-shrink-0',
                                        
                                        // If the button is SELECTED (Black background)
                                        activeFilter === filter.id
                                            ? 'tw-bg-white tw-bg-opacity-20 tw-text-white'
                                            : // If NOT selected, apply status-specific colors
                                            filter.id === 'high-demand'
                                            ? 'tw-bg-red-100 tw-text-red-600'
                                            : filter.id === 'warning'
                                            ? 'tw-bg-yellow-100 tw-text-yellow-700'
                                            : filter.id === 'active'
                                            ? 'tw-bg-green-100 tw-text-green-600'
                                            : filter.id === 'low-activity'
                                            ? 'tw-bg-gray-200 tw-text-gray-600'
                                            : 'tw-bg-gray-200 tw-text-gray-700'
                                    ]"
                                    style="font-size: 10px;"
                                >
                                    {{ filter.count }}
                                </span>
                            </button>
                        </div>
                    </div>
                </div>
                <ts-loading-banner :loading="loading" active>
                    <!-- Empty State -->
                    <div
                        v-if="filteredZones.length === 0"
                        class="tw-text-center tw-py-12"
                        style="min-height: 358px;"
                    >
                        <a-empty />
                    </div>
                    <div
                        class="tw-grid tw-grid-cols-1 tw-gap-6 md:tw-grid-cols-2 lg:tw-grid-cols-3 xl:tw-grid-cols-4 2xl:tw-grid-cols-5"
                    >
                        <div
                            v-for="zone in filteredZones"
                            :key="zone.id"
                            :class="[
                                'tw-group tw-relative tw-flex tw-flex-col tw-rounded-xl tw-border tw-bg-gray-50 dark:tw-bg-slate-700 dark:tw-border-slate-600 tw-shadow-sm tw-transition-all hover:tw-shadow-md hover:tw--translate-y-1 tw-overflow-hidden',

                                // High Demand -> Red Border
                                zone.dynamicStatus === 'High Demand'
                                    ? 'tw-border-red-100'
                                    : '',

                                // Warning -> Amber Border
                                zone.dynamicStatus === 'Warning'
                                    ? 'tw-border-amber-200'
                                    : '',

                                // NEW: Active -> Emerald Border
                                zone.dynamicStatus === 'Active'
                                    ? 'tw-border-green-100'
                                    : '',

                                // Default Border (only if not one of the above special statuses)
                                !['High Demand', 'Warning', 'Active'].includes(
                                    zone.dynamicStatus
                                )
                                    ? 'tw-border-slate-200'
                                    : '',

                                // Low Activity -> Dimmed effect
                                zone.dynamicStatus === 'Low Activity'
                                    ? 'tw-opacity-80 hover:tw-opacity-100'
                                    : ''
                            ]"
                        >
                            <div
                                v-if="zone.dynamicStatus === 'High Demand'"
                                class="tw-absolute tw-top-0 tw-left-0 tw-w-1 tw-h-full tw-bg-red-500"
                            ></div>
                            <div
                                v-if="zone.dynamicStatus === 'Warning'"
                                class="tw-absolute tw-top-0 tw-left-0 tw-w-1 tw-h-full tw-bg-yellow-400"
                            ></div>
                            <div
                                v-if="zone.dynamicStatus === 'Active'"
                                class="tw-absolute tw-top-0 tw-left-0 tw-w-1 tw-h-full tw-bg-green-500"
                            ></div>
                            <div class="tw-p-5 tw-flex tw-flex-col tw-h-full">
                                <div
                                    class="tw-mb-4 tw-flex tw-items-start tw-justify-between"
                                >
                                    <div>
                                        <h3
                                            class="tw-text-lg tw-font-bold tw-text-slate-900 dark:tw-text-white"
                                        >
                                            {{ zone.name }}
                                        </h3>
                                        <span
                                            class="tw-text-xs tw-font-medium tw-text-gray-500 dark:tw-text-gray-400"
                                        >
                                            {{ $t("zone.code") }}:
                                            {{ zone.zoneCode }}
                                        </span>
                                    </div>
                                    <div class="tw-relative">
                                        <button
                                            @click.stop="toggleMenu(zone.id)"
                                            class="tw-text-slate-400 hover:tw-text-slate-600 dark:hover:tw-text-slate-200 tw-p-1 tw-rounded-full hover:tw-bg-slate-100 dark:hover:tw-bg-slate-600 tw-transition-colors"
                                        >
                                            <span
                                                class="material-symbols-outlined"
                                                >more_vert</span
                                            >
                                        </button>

                                        <div
                                            v-if="activeMenuId === zone.id"
                                            v-click-outside="closeMenu"
                                            class="tw-absolute tw-right-0 tw-z-50 tw-mt-2 tw-w-40 tw-origin-top-right tw-rounded-lg tw-bg-white dark:tw-bg-slate-700 tw-shadow-xl tw-border tw-border-slate-100 dark:tw-border-slate-600 tw-py-1"
                                        >
                                            <button
                                                @click="editZone(zone)"
                                                class="tw-flex tw-w-full tw-items-center tw-gap-2 tw-px-4 tw-py-2 tw-text-sm tw-text-slate-700 dark:tw-text-slate-200 hover:tw-bg-blue-50 dark:hover:tw-bg-blue-900 hover:tw-text-blue-600 dark:hover:tw-text-blue-400 tw-transition-colors"
                                            >
                                                <span
                                                    class="material-symbols-outlined tw-text-[18px]"
                                                    >edit</span
                                                >
                                                {{ $t("zone.editZone") }}
                                            </button>
                                            <!-- Updated Delete Button with Popconfirm -->
                                            <a-popconfirm
                                                placement="rightTop"
                                                :title="
                                                    $t('are_you_sure_to_delete')
                                                "
                                                :ok-text="$t('yes')"
                                                :cancel-text="$t('no')"
                                                @confirm="deleteZone(zone)"
                                            >
                                                <button
                                                    :disabled="zone._deleting"
                                                    class="tw-flex tw-w-full tw-items-center tw-gap-2 tw-px-4 tw-py-2 tw-text-sm tw-text-red-600 hover:tw-bg-red-50 dark:hover:tw-bg-red-900 hover:tw-text-red-700 dark:hover:tw-text-red-400 tw-transition-colors"
                                                >
                                                    <i
                                                        class="fas fa-circle-notch fa-spin"
                                                        v-if="zone._deleting"
                                                    ></i>
                                                    <span
                                                        v-else
                                                        class="material-symbols-outlined tw-text-[18px]"
                                                        >delete</span
                                                    >
                                                    {{ $t("zone.deleteZone") }}
                                                </button>
                                            </a-popconfirm>
                                        </div>
                                    </div>
                                </div>

                                <div class="tw-mb-6 tw-flex-1">
                                    <div
                                        class="tw-flex tw-items-center tw-justify-between tw-mb-4"
                                    >
                                        <span
                                            class="tw-text-xs tw-font-bold tw-uppercase tw-tracking-wider tw-text-gray-700 dark:tw-text-gray-400"
                                            >{{ $t("zone.status") }}</span
                                        >
                                        <span
                                            :class="
                                                getStatusClass(
                                                    zone.dynamicStatus
                                                )
                                            "
                                        >
                                            <span
                                                v-if="
                                                    zone.dynamicStatus ===
                                                        'High Demand'
                                                "
                                                class="material-symbols-outlined tw-text-[16px] tw-text-current"
                                            >
                                                local_fire_department
                                            </span>
                                            <span>{{
                                                zone.dynamicStatus
                                            }}</span>
                                        </span>
                                    </div>
                                    <div class="tw-flex tw-flex-col">
                                        <span
                                            class="tw-text-sm tw-font-medium tw-text-slate-500 dark:tw-text-slate-300"
                                            >{{
                                                $t("zone.pendingPickup")
                                            }}</span
                                        >
                                        <span
                                            @click="showPendingPickups(zone)"
                                            :class="[
                                                'tw-text-4xl tw-font-black tw-leading-tight tw-mt-1 tw-cursor-pointer tw-transition-colors',
                                                zone.pendingPickups === 0
                                                    ? 'tw-text-slate-400 dark:tw-text-slate-500'
                                                    : 'tw-text-slate-900 dark:tw-text-white hover:tw-text-blue-600 dark:hover:tw-text-blue-400'
                                            ]"
                                        >
                                            {{ zone.pendingPickups }}
                                        </span>
                                    </div>
                                </div>

                                <div
                                    class="tw-border-t tw-border-slate-200 dark:tw-border-slate-600 tw-pt-4"
                                >
                                    <div
                                        class="tw-grid tw-grid-cols-2 tw-gap-4 tw-mb-4"
                                    >
                                        <div class="tw-flex tw-flex-col">
                                            <span
                                                class="tw-text-xs tw-text-gray-500 dark:tw-text-gray-400"
                                            >
                                                {{ $t("zone.totalToday") }}
                                            </span>
                                            <div
                                                @click="showTotalToday(zone)"
                                                :class="[
                                                    'tw-flex tw-items-center tw-gap-1.5 tw-font-bold tw-mt-1 tw-cursor-pointer tw-transition-colors',
                                                    zone.totalToday === 0
                                                        ? 'tw-text-slate-400 dark:tw-text-slate-500'
                                                        : 'tw-text-slate-700 dark:tw-text-slate-200 hover:tw-text-blue-600 dark:hover:tw-text-blue-400'
                                                ]"
                                            >
                                                <span
                                                    class="material-symbols-outlined tw-text-[18px] tw-text-blue-500"
                                                >
                                                    receipt_long
                                                </span>
                                                {{ zone.totalToday }}
                                            </div>
                                        </div>
                                        <div class="tw-flex tw-flex-col">
                                            <span
                                                class="tw-text-xs tw-text-gray-500 dark:tw-text-gray-400"
                                            >
                                                {{ $t("zone.totalShops") }}
                                            </span>
                                            <div
                                                @click="showTotalShops(zone)"
                                                :class="[
                                                    'tw-flex tw-items-center tw-gap-1.5 tw-font-bold tw-mt-1 tw-cursor-pointer tw-transition-colors',
                                                    zone.totalShops === 0
                                                        ? 'tw-text-slate-400 dark:tw-text-slate-500'
                                                        : 'tw-text-slate-700 dark:tw-text-slate-200 hover:tw-text-indigo-600 dark:hover:tw-text-indigo-400'
                                                ]"
                                            >
                                                <span
                                                    class="material-symbols-outlined tw-text-[18px] tw-text-indigo-500 dark:tw-text-indigo-400"
                                                >
                                                    storefront
                                                </span>
                                                {{ zone.totalShops }}
                                            </div>
                                        </div>
                                    </div>
                                    <button
                                        @click="assignDriver(zone)"
                                        class="tw-w-full tw-rounded-lg tw-py-2.5 tw-text-xs tw-font-bold tw-transition-colors tw-bg-white dark:tw-bg-gray-600 tw-text-gray-700 dark:tw-text-gray-200 hover:tw-bg-gray-100 dark:hover:tw-bg-gray-500 tw-shadow-sm"
                                    >
                                        {{ $t("zone.assignedDriver") }}
                                    </button>
                                </div>
                            </div>
                            <a-modal
                                v-model="show_pending_pickup_list"
                                centered
                                @ok="() => (show_pending_pickup_list = false)"
                                :zIndex="1020"
                                :cancelText="$t('cancel')"
                                :okText="$t('ok')"
                                :footer="null"
                                width="60%"
                            >
                                <template v-slot:title>
                                    <div
                                        class="tw-flex tw-justify-between tw-items-center tw-w-full"
                                    >
                                        <h2 class="tw-m-0">
                                            {{ $t("zone.pendingRequestList") }}
                                        </h2>
                                        <div
                                            class="tw-flex tw-gap-2 tw-text-sm tw-pr-10"
                                            v-if="selected_zone"
                                        >
                                            <h4 class="tw-m-0">
                                                {{ selected_zone.name }} |
                                            </h4>
                                            <h4 class="tw-m-0">
                                                {{ selected_zone.zoneCode }}
                                            </h4>
                                        </div>
                                    </div>
                                </template>
                                <ts-loading-banner
                                    :loading="pickup_list_loading"
                                >
                                    <pending-pickup-list
                                        :pendingpickup="pending_pickup_list"
                                    />
                                </ts-loading-banner>
                            </a-modal>
                            <a-modal
                                v-model="show_total_today_list"
                                centered
                                @ok="() => (show_total_today_list = false)"
                                :zIndex="1020"
                                :cancelText="$t('cancel')"
                                :okText="$t('ok')"
                                :footer="null"
                                width="70%"
                            >
                                <template v-slot:title>
                                    <div
                                        class="tw-flex tw-justify-between tw-items-center tw-w-full"
                                    >
                                        <h2 class="tw-m-0">
                                            {{ $t("zone.totalTodayList") }}
                                        </h2>
                                        <div
                                            class="tw-flex tw-gap-2 tw-text-sm tw-pr-10"
                                            v-if="selected_zone_for_today"
                                        >
                                            <h4 class="tw-m-0">
                                                {{
                                                    selected_zone_for_today.name
                                                }}
                                                |
                                            </h4>
                                            <h4 class="tw-m-0">
                                                {{
                                                    selected_zone_for_today.zoneCode
                                                }}
                                            </h4>
                                        </div>
                                    </div>
                                </template>
                                <ts-loading-banner
                                    :loading="total_today_loading"
                                >
                                    <today-request-list
                                        :todayrequests="total_today_list"
                                    />
                                </ts-loading-banner>
                            </a-modal>
                                <a-modal
                                v-model="show_total_shops_list"
                                centered
                                @ok="() => (show_total_shops_list = false)"
                                :zIndex="1020"
                                :cancelText="$t('cancel')"
                                :okText="$t('ok')"
                                :footer="null"
                                width="80%"
                            >
                                <template v-slot:title>
                                    <div
                                        class="tw-flex tw-justify-between tw-items-center tw-w-full"
                                    >
                                        <h2 class="tw-m-0">
                                            {{ $t("zone.totalShopsList") }}
                                        </h2>
                                        <div
                                            class="tw-flex tw-gap-2 tw-text-sm tw-pr-10"
                                            v-if="selected_zone_for_shops"
                                        >
                                            <h4 class="tw-m-0">
                                                {{
                                                    selected_zone_for_shops.name
                                                }}
                                                |
                                            </h4>
                                            <h4 class="tw-m-0">
                                                {{
                                                    selected_zone_for_shops.zoneCode
                                                }}
                                            </h4>
                                        </div>
                                    </div>
                                </template>
                                <ts-loading-banner
                                    :loading="total_shops_loading"
                                >
                                    <total-shop-list
                                        :totalShops="total_shops_list"
                                    />
                                </ts-loading-banner>
                            </a-modal>
                        </div>
                    </div>
                    <div class="d-flex justify-content-end tw-p-4">
                        <ts-pagination
                            v-model="pagination"
                            @navigate="onMounthfetch"
                        ></ts-pagination>
                    </div>
                </ts-loading-banner>
            </div>
        </main>
    </div>
</template>

<script>
import { mapState } from "vuex";
import { debounce } from "lodash";
import PendingPickupList from "./components/view-pending-list";
import TodayRequestList from "./components/view-today-request-list";
import TotalShopList from "./components/view-shop-list";
export default {
    name: "ZoneOperationsCenter",
    components: {
        PendingPickupList,
        TodayRequestList,
        TotalShopList
    },
    directives: {
        "click-outside": {
            bind(el, binding, vnode) {
                el.clickOutsideEvent = function(event) {
                    // Check that click was outside the el and its children
                    if (!(el == event.target || el.contains(event.target))) {
                        vnode.context[binding.expression](event);
                    }
                };
                document.body.addEventListener("click", el.clickOutsideEvent);
            },
            unbind(el) {
                document.body.removeEventListener(
                    "click",
                    el.clickOutsideEvent
                );
            }
        }
    },
    data() {
        return {
            loading: false,
            globalSearch: "",
            zoneFilter: "",
            refreshTimer: null,
            refreshInterval:
                localStorage.getItem("zone_refresh_interval") || "30",
            activeFilter: "all",
            activeMenuId: null,
            searchTimer: null,
            isInitialLoad: true,
            filters: [
                { id: "all", label: "All Zones" },
                { id: "high-demand", label: "High Demand" },
                { id: "warning", label: "Warning" },
                { id: "active", label: "Active" },
                { id: "low-activity", label: "Low Activity" }
            ],
            isRefreshing: false,
            show_pending_pickup_list: false,
            pending_pickup_list: [],
            pickup_list_loading: false,
            selected_zone: null,
            //totaltoday
            show_total_today_list: false,
            total_today_list: [],
            total_today_loading: false,
            selected_zone_for_today: null,
            //totalshop
            show_total_shops_list: false,
            total_shops_list: [],
            total_shops_loading: false,
            selected_zone_for_shops: null
        };
    },
    mounted() {
        this.onMounthfetch();
        this.startAutoRefresh();
    },
    beforeDestroy() {
        // Stop the timer to prevent memory leaks and unnecessary API calls
        this.stopAutoRefresh();
    },
    computed: {
        ...mapState("driver/zone", ["resources", "pagination"]),
        search: {
            get() {
                return this.$store.state.driver.zone.search;
            },
            set(newValue) {
                this.$store.commit("driver/zone/SET_SEARCH", newValue);
                this.$store.commit("driver/zone/RESET_CURRENT_PAGE");
            }
        },
        filtersWithCounts() {
            return this.filters.map(filter => {
                let count = null; // Default to null instead of 0

                if (filter.id !== "all") {
                    const statusMap = {
                        "high-demand": "High Demand",
                        warning: "Warning",
                        active: "Active",
                        "low-activity": "Low Activity"
                    };

                    const targetStatus = statusMap[filter.id];
                    if (targetStatus) {
                        count = this.zones.filter(
                            z => z.dynamicStatus === targetStatus
                        ).length;
                    }
                }

                return { ...filter, count };
            });
        },

        filteredZones() {
            let filtered = this.zones;

            // Apply text search filter
            if (this.search) {
                const searchLower = this.search.toLowerCase();
                filtered = filtered.filter(
                    zone =>
                        zone.name.toLowerCase().includes(searchLower) ||
                        zone.zoneCode.toLowerCase().includes(searchLower) ||
                        zone.id
                            .toString()
                            .toLowerCase()
                            .includes(searchLower)
                );
            }

            // Apply status filter
            if (this.activeFilter !== "all") {
                const statusMap = {
                    "high-demand": "High Demand",
                    warning: "Warning",
                    active: "Active",
                    "low-activity": "Low Activity" // Add this
                };

                const targetStatus = statusMap[this.activeFilter];
                if (targetStatus) {
                    filtered = filtered.filter(
                        z => z.dynamicStatus === targetStatus
                    );
                }
            }

            return filtered;
        },

        zones() {
            if (!this.resources || this.resources.length === 0) {
                return [];
            }

            return this.resources.map(zone => {
                const pendingPickups = zone.pending_pickups_count || 0;

                // Calculate dynamic status based on pending pickups
                let dynamicStatus;
                if (pendingPickups >= 10) {
                    dynamicStatus = "High Demand";
                } else if (pendingPickups >= 6 && pendingPickups <= 9) {
                    dynamicStatus = "Warning";
                } else if (pendingPickups >= 1 && pendingPickups <= 5) {
                    dynamicStatus = "Active";
                } else {
                    dynamicStatus = "Low Activity";
                }

                return {
                    id: zone.id,
                    zoneCode: zone.zone_code || "N/A",
                    name: zone.name,
                    remarks: zone.remarks,
                    status: zone.status === "active" ? "Active" : "Inactive", // Original status from DB
                    dynamicStatus: dynamicStatus, // New dynamic status based on pickups
                    totalShops: zone.total_shops_count
                        ? zone.total_shops_count
                        : 0,
                    pendingPickups: pendingPickups,
                    totalToday: zone.total_today_count
                        ? zone.total_today_count
                        : 0,
                    shops: zone.shops || [],
                    pickUpRequest: zone.pickup_requests || []
                };
            });
        }
    },
    methods: {
        async fetchData(attributes) {
            return this.$store
                .dispatch("driver/zone/fetch", { ...attributes })
                .catch(error => {
                    this.$notify({ type: "error", text: error.message });
                });
        },

        async onMounthfetch(attributes = {}) {
            this.loading = true;
            await this.fetchData(attributes);
            this.loading = false;
        },

        getStatusClass(status) {
            const baseClass =
                "tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded-full tw-px-3 tw-py-1 tw-text-xs tw-font-bold";

            switch (status) {
                case "High Demand":
                    // tw-bg-red-50 is the very light pink/red background
                    // tw-text-red-700 is a deep, readable red
                    return `${baseClass} tw-bg-red-50 tw-text-red-700 dark:tw-bg-red-900 dark:tw-bg-opacity-30 dark:tw-text-red-400`;
                case "Warning":
                    return `${baseClass} tw-bg-yellow-50 tw-text-yellow-700 dark:tw-bg-yellow-900 dark:tw-bg-opacity-30 dark:tw-text-yellow-400`;
                case "Active":
                    return `${baseClass} tw-bg-green-50 tw-text-green-700 dark:tw-bg-green-900 dark:tw-bg-opacity-30 dark:tw-text-green-400`;
                default:
                    return `${baseClass} tw-bg-gray-100 tw-text-gray-600 dark:tw-bg-gray-700 dark:tw-text-gray-400`;
            }
        },
        addNewZone() {
            this.$router.push({ name: "zone-create" });
            // Implement add zone logic
        },
        assignDriver(zone) {
            this.$router.push({
                name: "assign-driver",
                query: { id: zone.id }
            });
            // Implement driver assignment logic
        },

        toggleMenu(id) {
            // If clicking the same button, close it; otherwise open the new one
            this.activeMenuId = this.activeMenuId === id ? null : id;
        },
        closeMenu() {
            this.activeMenuId = null;
        },
        editZone(zone) {
            this.closeMenu();
            // Navigate immediately - since loading is false, Popconfirm won't crash
            this.$router.push({ name: "zone-edit", params: { id: zone.id } });
        },
        confirmDelete(zone) {
            this.closeMenu();
            // Use a simple confirm or a custom modal
            if (confirm(`Are you sure you want to delete ${zone.name}?`)) {
                this.deleteZone(zone);
            }
        },

        async deleteZone(zone) {
            this.closeMenu();
            this.loading = true;
            await this.$store
                .dispatch("driver/zone/destroy", zone.id)
                .then(response => {
                    this.$notify({ type: "success", text: response.message });
                    return this.fetchData();
                })
                .catch(error => {
                    this.$notify({ type: "error", text: error.message });
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        startAutoRefresh() {
            this.stopAutoRefresh(); // Now this will work
            const seconds = parseInt(this.refreshInterval);
            if (isNaN(seconds)) return;

            this.refreshTimer = setInterval(() => {
                if (!this.loading && !this.isRefreshing) {
                    this.fetchData(null, true);
                }
            }, seconds * 1000);
        },

        stopAutoRefresh() {
            if (this.refreshTimer) {
                clearInterval(this.refreshTimer);
                this.refreshTimer = null;
            }
        },

        showPendingPickups(zone) {
            this.show_pending_pickup_list = true;
            this.pickup_list_loading = true;
            this.selected_zone = zone;
            this.$store
                .dispatch("driver/zone/getRequestDetail", {
                    id: zone.id
                })
                .then(response => {
                    this.pending_pickup_list = response.data || [];
                })
                .finally(() => {
                    this.pickup_list_loading = false;
                })
                .catch(error => {
                    this.$notify({ type: "error", text: error.message });
                });
        },
        showTotalToday(zone) {
            this.show_total_today_list = true;
            this.total_today_loading = true;
            this.selected_zone_for_today = zone;

            // Replace with your actual API endpoint
            this.$store
                .dispatch("driver/zone/getTodayRequestsDetail", {
                    id: zone.id
                })
                .then(response => {
                    this.total_today_list = response.data || [];
                })
                .finally(() => {
                    this.total_today_loading = false;
                })
                .catch(error => {
                    this.$notify({ type: "error", text: error.message });
                    this.total_today_list = [];
                });
        },
        showTotalShops(zone) {
            this.show_total_shops_list = true;
            this.total_shops_loading = true;
            this.selected_zone_for_shops = zone;
        
        this.$store
            .dispatch("driver/zone/getShopsDetail", {
                id: zone.id
            })
            .then(response => {
                this.total_shops_list = response.data || [];
            })
            .finally(() => {
                this.total_shops_loading = false;
            })
            .catch(error => {
                this.$notify({ type: "error", text: error.message });
                this.total_shops_list = [];
            });
        },
    },
    watch: {
        search: debounce(function() {
            this.onMounthfetch();
        }, 500),
        refreshInterval: {
            handler(newVal) {
                // 2. Save the selection to localStorage whenever it changes
                localStorage.setItem("zone_refresh_interval", newVal);

                // Restart the timer with the new interval
                this.startAutoRefresh();
            }
        }
    }
};
</script>

<style scoped>
/* Add any component-specific styles here if needed */
.scrollbar-hide::-webkit-scrollbar {
    display: none;
}
.scrollbar-hide {
    -ms-overflow-style: none;
    scrollbar-width: none;
}
</style>
